/** Mostrar listas e historico: abas, tabela que vira cartao no celular e a trilha (linha do tempo). */
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { LinkInterno } from "../navegacao";
import type { Tom } from "./Basicos";
import { Paginacao, paginar, type PaginacaoDaLista } from "./Paginacao";

export type Aba<T extends string> = { valor: T; rotulo: string; contagem?: number };

export type AbaDeLink = { href: string; rotulo: string; atual?: boolean; contagem?: number };

/**
 * Abas que sao links, uma por endereco. Servem quando cada aba e uma tela de verdade (a pessoa copia o link, volta pelo
 * botao do navegador, chega de um e-mail): o menu principal aponta pro assunto e as abas abrem as partes dele.
 */
export function AbasDeLink({ abas, rotulo }: { abas: AbaDeLink[]; rotulo: string }) {
  return (
    <nav className="mtc-abas" aria-label={rotulo}>
      {abas.map((a) => (
        <LinkInterno key={a.href} href={a.href} className="mtc-aba" aria-current={a.atual ? "page" : undefined}>
          {a.rotulo}
          {a.contagem !== undefined && <span className="mtc-aba__contagem">{a.contagem}</span>}
        </LinkInterno>
      ))}
    </nav>
  );
}

/** Abas de uma lista (pendentes, aguardando, decididas). Setas do teclado trocam de aba. */
export function Abas<T extends string>({ abas, valor, aoMudar, rotulo }: { abas: Aba<T>[]; valor: T; aoMudar: (v: T) => void; rotulo: string }) {
  const teclar = (e: KeyboardEvent, i: number) => {
    const passo = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!passo) return;
    const proxima = abas[(i + passo + abas.length) % abas.length]!;
    aoMudar(proxima.valor);
    (e.currentTarget.parentElement?.children[(i + passo + abas.length) % abas.length] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className="mtc-abas" role="tablist" aria-label={rotulo}>
      {abas.map((a, i) => (
        <button key={a.valor} type="button" role="tab" className="mtc-aba" aria-selected={a.valor === valor} tabIndex={a.valor === valor ? 0 : -1} onClick={() => aoMudar(a.valor)} onKeyDown={(e) => teclar(e, i)}>
          {a.rotulo}
          {a.contagem !== undefined && <span className="mtc-aba__contagem">{a.contagem}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Por que algo nao deu certo, numa celula ou num aviso: o que aconteceu em uma linha e o detalhe (quem respondeu, com
 * o codigo) embaixo. Ex.: "Limite do PIX de teste" e "A Pagar.me de teste so gera PIX ate R$ 500,00...".
 */
export function Motivo({ titulo, detalhe }: { titulo: string; detalhe?: ReactNode }) {
  return (
    <span className="mtc-motivo">
      <span className="mtc-motivo__titulo">{titulo}</span>
      {detalhe && <span className="mtc-motivo__detalhe" title={typeof detalhe === "string" ? detalhe : undefined}>{detalhe}</span>}
    </span>
  );
}

/** `rotuloOculto`: o cabecalho fica so pro leitor de tela e, no cartao, a celula ocupa a largura toda (coluna de acoes). */
export type Coluna<L> = { chave: string; rotulo: string; celula: (l: L) => ReactNode; alinhar?: "esquerda" | "direita"; rotuloOculto?: boolean };

/**
 * Tabela de dados. No celular (ate 640 px) cada linha vira um cartao com o rotulo ao lado do valor. `cartao="media"`: vira
 * cartao quando a propria caixa tem ate 840 px, pra tabela com muita coisa por linha (janela estreita, painel lateral).
 * `aoClicar` deixa a linha inteira clicavel; ponha tambem um link numa celula, pra quem usa teclado.
 */
export function Tabela<L>({ colunas, linhas, chave, aoClicar, destaque, vazio, cartao, paginacao }: { colunas: Coluna<L>[]; linhas: L[]; chave: (l: L) => string; aoClicar?: (l: L) => void; destaque?: (l: L) => boolean; cartao?: "estreita" | "media"; vazio?: ReactNode; /** De 20 em 20 (spec 058). */ paginacao?: PaginacaoDaLista }) {
  const topo = useRef<HTMLDivElement>(null);
  if (!linhas.length && vazio) return <>{vazio}</>;
  const total = paginacao ? (paginacao.total ?? linhas.length) : linhas.length;
  const visiveis = paginacao && paginacao.total == null ? paginar(linhas, paginacao.pagina) : linhas;
  return (
    <>
    <div className="mtc-tabela-caixa" ref={topo}>
      <table className="mtc-tabela" data-cartao={cartao === "media" ? "media" : undefined}>
        <thead>
          <tr>{colunas.map((c) => <th key={c.chave} scope="col" data-alinhar={c.alinhar}>{c.rotuloOculto ? <span className="mtc-oculto">{c.rotulo}</span> : c.rotulo}</th>)}</tr>
        </thead>
        <tbody>
          {visiveis.map((l) => (
            <tr key={chave(l)} data-clicavel={aoClicar ? true : undefined} data-destaque={destaque?.(l) || undefined} onClick={aoClicar ? (e) => { if (!(e.target as HTMLElement).closest("a, button")) aoClicar(l); } : undefined}>
              {colunas.map((c) => <td key={c.chave} data-rotulo={c.rotuloOculto ? undefined : c.rotulo} data-sem-rotulo={c.rotuloOculto || undefined} data-alinhar={c.alinhar}><div className="mtc-tabela__celula">{c.celula(l)}</div></td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {paginacao && <Paginacao pagina={paginacao.pagina} total={total} rotulo={paginacao.rotulo} aoMudar={(p) => { paginacao.aoMudar(p); voltarAoTopo(topo.current); }} />}
    </>
  );
}

/** Trocou de pagina la embaixo: a tabela volta pro comeco, sem passar por baixo das faixas e da barra do topo. */
export function voltarAoTopo(el: HTMLElement | null) {
  if (!el || typeof window === "undefined") return;
  const topo = el.getBoundingClientRect().top;
  if (topo < 0) window.scrollTo({ top: window.scrollY + topo - 96, behavior: "smooth" });
}

export type Marco = { chave: string; titulo: ReactNode; detalhe?: ReactNode; quando?: ReactNode; tom?: Tom };

/** Trilha: o que aconteceu, em ordem (mudancas de status, auditoria, decisoes). */
export function LinhaDoTempo({ itens, vazio = "Nada registrado ainda." }: { itens: Marco[]; vazio?: string }) {
  if (!itens.length) return <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>{vazio}</p>;
  return (
    <ol className="mtc-trilha">
      {itens.map((m) => (
        <li key={m.chave} data-tom={m.tom}>
          <span className="mtc-trilha__titulo">{m.titulo}</span>
          {m.detalhe && <span className="mtc-trilha__detalhe">{m.detalhe}</span>}
          {m.quando && <span className="mtc-trilha__quando">{m.quando}</span>}
        </li>
      ))}
    </ol>
  );
}

export type OpcaoDeEscolha = { valor: string; titulo: ReactNode; detalhe?: ReactNode; selo?: ReactNode; desabilitada?: boolean; motivo?: ReactNode };

/**
 * Escolha entre cartoes (radio nativo: setas do teclado trocam). A opcao desabilitada continua visivel, com o motivo,
 * pra quem opera entender por que nao pode escolher (ex.: a conta que nao serve pra esta etapa).
 */
export function ListaDeEscolha({ rotulo, nome, opcoes, valor, aoMudar, dica, acoes }: { rotulo: string; nome: string; opcoes: OpcaoDeEscolha[]; valor: string; aoMudar: (v: string) => void; dica?: ReactNode; acoes?: ReactNode }) {
  const id = useId();
  return (
    <div className="mtc-escolha">
      <div className="mtc-escolha__cabeca">
        <span id={id} className="mtc-campo__rotulo">{rotulo}</span>
        {acoes}
      </div>
      <div className="mtc-escolha__lista" role="radiogroup" aria-labelledby={id}>
        {opcoes.map((o) => (
          <label key={o.valor} className="mtc-escolha__opcao" data-desabilitada={o.desabilitada || undefined} data-escolhida={valor === o.valor || undefined}>
            <input type="radio" name={nome} value={o.valor} checked={valor === o.valor} disabled={o.desabilitada} onChange={() => aoMudar(o.valor)} />
            <span className="mtc-escolha__marca" aria-hidden />
            <span className="mtc-escolha__textos">
              <span className="mtc-escolha__titulo">{o.titulo}</span>
              {o.detalhe && <span className="mtc-escolha__detalhe">{o.detalhe}</span>}
              {o.desabilitada && o.motivo && <span className="mtc-escolha__motivo">{o.motivo}</span>}
            </span>
            {o.selo && <span className="mtc-escolha__selo">{o.selo}</span>}
          </label>
        ))}
      </div>
      {dica && <span className="mtc-campo__dica">{dica}</span>}
    </div>
  );
}
